import { CalendarDays, Eye, Heart, Star, ImagePlus, ChevronRight, Ticket, Tag, MessageSquare } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const ICONS = { vues: Eye, reservations: CalendarDays, favoris: Heart, avis: Star };
const COLORS = {
  vues: ["#7C3AED", "#F5F3FF"],
  reservations: ["#16A34A", "#F0FDF4"],
  favoris: ["#EF4444", "#FEF2F2"],
  avis: ["#F59E0B", "#FFFBEB"],
};

export const ApercuView = ({ data, go }) => {
  const { t, lang } = useLanguage();
  const feed = [
    ...data.reviews.slice(0, 4).map((r) => ({
      icon: MessageSquare,
      text: `Nouvel avis ${r.rating}★ de ${r.name}`,
      sub: r.date,
    })),
    ...data.reservations.slice(0, 4).map((r) => ({
      icon: Ticket,
      text: `Réservation — ${r.client}, ${r.guests} pers.`,
      sub: r.event_title || "",
    })),
  ];

  return (
    <div className="mt-6 space-y-5" data-testid="pro-apercu-view">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {data.kpis.map((k) => {
          const Icon = ICONS[k.key];
          const [color, bg] = COLORS[k.key];
          return (
            <div key={k.key} className="rounded-3xl border border-slate-100 bg-white p-4 shadow-sm">
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl" style={{ backgroundColor: bg }}>
                <Icon size={18} style={{ color }} />
              </div>
              <p className="mt-3 text-xs font-medium text-slate-500">{k.label}</p>
              <p className="mt-0.5 text-xl font-extrabold text-slate-900">{k.value}</p>
              {k.sub && <p className="text-[10px] text-slate-400">{k.sub}</p>}
              <p className="mt-0.5 text-[10px] font-semibold text-emerald-600">{k.delta}</p>
            </div>
          );
        })}
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900">{t("Activité récente")}</h3>
          <div className="mt-3 space-y-1">
            {feed.length === 0 && <p className="rounded-2xl bg-[#F8FAFC] p-6 text-center text-xs text-slate-400">{t("Aucune activité pour l'instant — partage ta page pour attirer des visiteurs !")}</p>}
            {feed.map((f, i) => (
              <div key={i} data-testid={`feed-item-${i}`} className="flex items-center gap-3 rounded-2xl px-2 py-2.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#EFF6FF]">
                  <f.icon className="h-4 w-4 text-[#2563EB]" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-slate-800">{f.text}</p>
                  <p className="truncate text-[10px] text-slate-400">{f.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900">{t("Actions suggérées")}</h3>
          <div className="mt-3 space-y-1">
            {[
              { icon: CalendarDays, title: "Publier un événement", sub: "Attire 3x plus de vues", to: "evenements" },
              { icon: ImagePlus, title: "Ajouter des photos", sub: "Les fiches avec 5+ photos performent mieux", to: "photos" },
              { icon: Tag, title: "Créer une promotion", sub: "Booste tes réservations", to: "promotions" },
            ].map((a) => (
              <button
                key={a.to}
                type="button"
                data-testid={`apercu-action-${a.to}`}
                onClick={() => go(a.to)}
                className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition hover:bg-slate-50"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#EFF6FF]">
                  <a.icon className="h-4 w-4 text-[#2563EB]" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-800">{a.title}</span>
                  <span className="block text-[10px] text-slate-400">{a.sub}</span>
                </span>
                <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
